<template>
  <div class="pre-audit-guidance">
    <!-- Header -->
    <div class="bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex flex-col space-y-4">
          <a @click="$emit('back')" href="javascript:void(0)" class="flex items-center text-sm text-gray-500 hover:text-gray-700 self-start cursor-pointer">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to Farm Details
          </a>
          <h1 class="text-xl font-medium text-gray-900">Pre-audit guidance for <span class="text-[#3A895F]">{{ farmName }}</span></h1>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 bg-white" style="border-left: none;">
      <!-- Farm Info -->
      <div class="mb-6">
        <div class="space-y-2">
          <p class="text-sm text-gray-500">Client: <span class="text-[#262F2A] font-bold">ACME Systems Company (on behalf of DAFF)</span></p>
          <p class="text-sm text-gray-500">Property: <span class="text-[#262F2A] font-bold">{{ farmName }} ({{ trustName }})</span></p>
          <p class="text-sm text-gray-500">Program: <span class="text-[#262F2A] font-bold">AUS-MEAT MSW (Wool/Sheep)</span></p>
          <p class="text-sm text-gray-500">Audit Type: <span class="text-[#262F2A] font-bold">Routine LPA / Biosecurity Verification — Targeted / Data-Optimised</span></p>
          <p class="text-sm text-gray-500">Date of Visit: <span class="text-[#262F2A] font-bold">23.10.25</span></p>
          <p class="text-sm text-gray-500">Assigned Auditor: <span class="text-[#262F2A] font-bold">Jane Cooper</span></p>
        </div>
      </div>

      <!-- Description -->
      <div class="mb-6">
        <p class="text-sm text-gray-700">Perform a targeted on-site verification of {{ farmName }}'s LPA and Biosecurity compliance, focusing only on unverified or inconsistent data points<br>identified in corrective database and prior audit records.</p>
      </div>

      <!-- Goal -->
      <div class="mb-6">
        <p class="text-sm text-gray-700"><span class="font-medium">Goal:</span> reduce time on confirmed-compliant areas; validate missing or contradictory records.</p>
      </div>

      <!-- Data Gaps & Focus Areas -->
      <div class="mb-6 border border-[#F5F8F7] rounded-md">
        <div 
          @click="toggleSection('dataGaps')"
          class="flex justify-between items-center px-4 py-3 cursor-pointer"
          :class="{'bg-gray-50 border-b border-[#F5F8F7]': isExpanded('dataGaps'), 'bg-[#F5F8F7]': !isExpanded('dataGaps')}"
        >
          <h2 class="text-sm font-medium text-gray-900">Data Gaps & Focus Areas for Field Audit</h2>
          <button class="text-[#3A895F] hover:text-green-700">
            <svg v-if="isExpanded('dataGaps')" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div v-if="isExpanded('dataGaps')" class="p-4 bg-[#F5F8F7]">
          <!-- Biosecurity Records -->
          <div class="mb-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="biosecurity-records" v-model="checkboxes.biosecurityRecords" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="biosecurity-records" class="font-medium text-[#3A895F]">Biosecurity Records</label>
                <p class="text-sm text-gray-500 mt-1">Visitor log incomplete (no entries between May-Aug 2025)</p>
                <p class="text-sm text-gray-500">No photographs or written evidence of disinfection points at main gate.</p>
                <p class="text-sm font-medium text-gray-700 mt-2">Action: Verify physical presence and functionality of disinfection station; inspect sign-in procedures.</p>
              </div>
            </div>
          </div>

          <!-- Animal Treatments & Medication Records -->
          <div class="mb-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="animal-treatments" v-model="checkboxes.animalTreatments" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="animal-treatments" class="font-medium text-[#3A895F]">Animal Treatments & Medication Records</label>
                <p class="text-sm text-gray-500 mt-1">Records after Dec 2024 missing from LPA upload.</p>
                <p class="text-sm text-gray-500">Random vet visit (A. Martinez, Mar 2025) referenced treatment with oxytetracycline — no matching entry in on-farm log.</p>
                <p class="text-sm font-medium text-gray-700 mt-2">Action: Request to view manual or digital treatment records (Mar-Oct 2025 period). Cross-check batch numbers and withholding periods.</p>
              </div>
            </div>
          </div>

          <!-- NLIS / Animal Movements -->
          <div class="mb-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="nlis-movements" v-model="checkboxes.nlisMovements" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="nlis-movements" class="font-medium text-[#3A895F]">NLIS / Animal Movements</label>
                <p class="text-sm text-gray-500 mt-1">Two outgoing consignments (April and June 2025) show unconfirmed receipt PICs in NLIS.</p>
                <p class="text-sm font-medium text-gray-700 mt-2">Action: Verify consignment records (eNVD, transporter dockets) and confirm final destinations.</p>
              </div>
            </div>
          </div>

          <!-- Feed & Cropping Records -->
          <div class="mb-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="feed-cropping" v-model="checkboxes.feedCropping" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="feed-cropping" class="font-medium text-[#3A895F]">Feed & Cropping Records</label>
                <p class="text-sm text-gray-500 mt-1">Crop input data for 2025 winter season missing.</p>
                <p class="text-sm text-gray-500">Previous records show mixed feed sources but no current supplier receipts.</p>
                <p class="text-sm font-medium text-gray-700 mt-2">Action: Confirm cropping inputs, supplier invoices, and residue risk management steps.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Historical Risk Indicators -->
      <div class="mb-6 border border-[#F5F8F7] rounded-md">
        <div 
          @click="toggleSection('historicalRisk')"
          class="flex justify-between items-center px-4 py-3 cursor-pointer"
          :class="{'bg-gray-50 border-b border-[#F5F8F7]': isExpanded('historicalRisk'), 'bg-[#F5F8F7]': !isExpanded('historicalRisk')}"
        >
          <h2 class="text-sm font-medium text-gray-900">Historical Risk Indicators</h2>
          <button class="text-[#3A895F] hover:text-green-700">
            <svg v-if="isExpanded('historicalRisk')" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div v-if="isExpanded('historicalRisk')" class="p-4 bg-[#F5F8F7]">
          <p class="text-sm text-gray-700">No significant historical risk indicators found for this property.</p>
        </div>
      </div>

      <!-- Recommended Audit Approach -->
      <div class="mb-6 border border-[#F5F8F7] rounded-md">
        <div 
          @click="toggleSection('auditApproach')"
          class="flex justify-between items-center px-4 py-3 cursor-pointer"
          :class="{'bg-gray-50 border-b border-[#F5F8F7]': isExpanded('auditApproach'), 'bg-[#F5F8F7]': !isExpanded('auditApproach')}"
        >
          <h2 class="text-sm font-medium text-gray-900">Recommended Audit Approach (Time-Efficient)</h2>
          <button class="text-[#3A895F] hover:text-green-700">
            <svg v-if="isExpanded('auditApproach')" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div v-if="isExpanded('auditApproach')" class="p-4 bg-[#F5F8F7]">
          <ul class="list-disc pl-5 text-sm text-gray-700 space-y-2">
            <li>Focus on the specific data gaps identified rather than a full property inspection</li>
            <li>Schedule visit during regular farm operations (not shearing or mustering days)</li>
            <li>Request digital records be prepared in advance where possible</li>
            <li>Allow 2-3 hours for the targeted verification process</li>
          </ul>
        </div>
      </div>

      <!-- Required Evidence to Collect -->
      <div class="mb-6 border border-[#F5F8F7] rounded-md">
        <div 
          @click="toggleSection('requiredEvidence')"
          class="flex justify-between items-center px-4 py-3 cursor-pointer"
          :class="{'bg-gray-50 border-b border-[#F5F8F7]': isExpanded('requiredEvidence'), 'bg-[#F5F8F7]': !isExpanded('requiredEvidence')}"
        >
          <h2 class="text-sm font-medium text-gray-900">Required Evidence to Collect / Capture</h2>
          <button class="text-[#3A895F] hover:text-green-700">
            <svg v-if="isExpanded('requiredEvidence')" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div v-if="isExpanded('requiredEvidence')" class="p-4 bg-[#F5F8F7]">
          <div class="space-y-3">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="photos-entry" v-model="checkboxes.photosEntry" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="photos-entry" class="text-sm text-gray-700">Photos of entry signage, footbath or disinfection facilities</label>
              </div>
            </div>
            
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="copies-treatment" v-model="checkboxes.copiesTreatment" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="copies-treatment" class="text-sm text-gray-700">Copies or screenshots of treatment log pages (March-Oct 2025)</label>
              </div>
            </div>
            
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="supplier-invoices" v-model="checkboxes.supplierInvoices" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="supplier-invoices" class="text-sm text-gray-700">Supplier invoices or receipts for feed/fertilizer</label>
              </div>
            </div>
            
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="nlis-reference" v-model="checkboxes.nlisReference" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="nlis-reference" class="text-sm text-gray-700">NLIS or eNVD reference numbers for April/June consignments</label>
              </div>
            </div>
            
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="signed-training" v-model="checkboxes.signedTraining" type="checkbox" class="focus:ring-[#3A895F] h-4 w-4 text-[#3A895F] border-gray-300 rounded" style="accent-color: #3A895F;">
              </div>
              <div class="ml-3">
                <label for="signed-training" class="text-sm text-gray-700">Signed training register or evidence of induction completion</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reporting Instructions -->
      <div class="mb-6 border border-[#F5F8F7] rounded-md">
        <div 
          @click="toggleSection('reportingInstructions')"
          class="flex justify-between items-center px-4 py-3 cursor-pointer"
          :class="{'bg-gray-50 border-b border-[#F5F8F7]': isExpanded('reportingInstructions'), 'bg-[#F5F8F7]': !isExpanded('reportingInstructions')}"
        >
          <h2 class="text-sm font-medium text-gray-900">Reporting Instructions</h2>
          <button class="text-[#3A895F] hover:text-green-700">
            <svg v-if="isExpanded('reportingInstructions')" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div v-if="isExpanded('reportingInstructions')" class="p-4 bg-[#F5F8F7]">
          <ul class="list-disc pl-5 text-sm text-gray-700 space-y-2">
            <li>Complete audit report within 48 hours of visit</li>
            <li>Upload all evidence photos to the secure AUS-MEAT portal</li>
            <li>Flag any critical non-conformities immediately via phone</li>
            <li>Use the standardized reporting template with all sections completed</li>
          </ul>
        </div>
      </div>

      <!-- Actions -->
      <div class="mt-8">
        <ul class="space-y-0 border-t border-gray-200">
          <li class="flex justify-between items-center py-4 border-b border-gray-200">
            <a href="#" style="color: #145E37" class="hover:underline">Generate a printable PDF template with placeholders for PIC, audit date, and auto-tick boxes</a>
            <div class="flex justify-end w-8">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.47917 8L6.16667 10.3125L7.22917 11.375L10.6042 8L7.22917 4.625L6.16667 5.6875L8.47917 8ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16ZM8 14.5C9.80556 14.5 11.3403 13.8681 12.6042 12.6042C13.8681 11.3403 14.5 9.80556 14.5 8C14.5 6.19444 13.8681 4.65972 12.6042 3.39583C11.3403 2.13194 9.80556 1.5 8 1.5C6.19444 1.5 4.65972 2.13194 3.39583 3.39583C2.13194 4.65972 1.5 6.19444 1.5 8C1.5 9.80556 2.13194 11.3403 3.39583 12.6042C4.65972 13.8681 6.19444 14.5 8 14.5Z" fill="#3A895F"/>
              </svg>
            </div>
          </li>
          <li class="flex justify-between items-center py-4 border-b border-gray-200">
            <a href="#" style="color: #145E37" class="hover:underline">Send the action points to AUS-MEAT digital platform mobile app</a>
            <div class="flex justify-end w-8">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.47917 8L6.16667 10.3125L7.22917 11.375L10.6042 8L7.22917 4.625L6.16667 5.6875L8.47917 8ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16ZM8 14.5C9.80556 14.5 11.3403 13.8681 12.6042 12.6042C13.8681 11.3403 14.5 9.80556 14.5 8C14.5 6.19444 13.8681 4.65972 12.6042 3.39583C11.3403 2.13194 9.80556 1.5 8 1.5C6.19444 1.5 4.65972 2.13194 3.39583 3.39583C2.13194 4.65972 1.5 6.19444 1.5 8C1.5 9.80556 2.13194 11.3403 3.39583 12.6042C4.65972 13.8681 6.19444 14.5 8 14.5Z" fill="#3A895F"/>
              </svg>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PreAuditGuidance',
  props: {
    farmName: {
      type: String,
      default: 'Ironbark Station'
    },
    processedAuditReport: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      expandedSections: {
        dataGaps: true,
        historicalRisk: false,
        auditApproach: false,
        requiredEvidence: true,
        reportingInstructions: false
      },
      checkboxes: {
        biosecurityRecords: false,
        animalTreatments: false,
        nlisMovements: false,
        feedCropping: false,
        photosEntry: false,
        copiesTreatment: false,
        supplierInvoices: false,
        nlisReference: false,
        signedTraining: false
      }
    }
  },
  computed: {
    trustName() {
      // Farm owner mapping
      const farmOwnerMapping = {
        'Kookaburra Plains': 'Ash & Mia Robertson',
        'Bluegum Ridge': 'Bluegum Ag Unit Trust',
        'Banksia Vale': 'Lee & Carter Pty Ltd',
        'Eucalyptus Park': 'Euca Holdings Pty Ltd',
        'Ironbark Station': 'Nguyen Family Trust',
        'Redbank Hill': 'Redbank Hill Estates',
        'Rivergum Plains': 'Rivergum Pastures Pty Ltd',
        'Snowy View': 'Snowy View Holdings',
        'Wattle Creek': 'Wattle Creek Family Trust',
        'Waratah Downs': 'Waratah Pastoral Co.'
      };
      
      return farmOwnerMapping[this.farmName] || 'Unknown Owner';
    }
  },
  watch: {
    processedAuditReport: {
      handler(newReport) {
        if (newReport) {
          console.log('PreAuditGuidance: Received new audit report', newReport);
          this.processAuditReport(newReport);
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Scroll to top when component is mounted
    window.scrollTo(0, 0);
  },
  methods: {
    toggleSection(section) {
      this.expandedSections[section] = !this.expandedSections[section];
    },
    isExpanded(section) {
      return this.expandedSections[section];
    },
    processAuditReport(report) {
      console.log('PreAuditGuidance: Processing audit report:', report);
      
      // In a real implementation, we would analyze the file contents
      // For this demo, we'll read the file and check for COMPLIANT status
      
      // Create a FileReader to read the file contents
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target.result;
        console.log('PreAuditGuidance: Report content length:', content.length);
        console.log('PreAuditGuidance: Report content preview:', content.substring(0, 100));
        
        // Check for compliance status in each section
        const biosecurityCompliant = content.includes('BIOSECURITY RECORDS') && 
                                    content.includes('Status: COMPLIANT');
        
        const animalTreatmentsCompliant = content.includes('ANIMAL TREATMENTS & MEDICATION RECORDS') && 
                                         content.includes('Status: COMPLIANT');
        
        const nlisMovementsCompliant = content.includes('NLIS / ANIMAL MOVEMENTS') && 
                                      content.includes('Status: COMPLIANT');
        
        const feedCroppingCompliant = content.includes('FEED & CROPPING RECORDS') && 
                                     content.includes('Status: COMPLIANT');
        
        // Update checkboxes based on the analysis
        console.log('PreAuditGuidance: Updating checkboxes based on compliance status:',
          { biosecurityCompliant, animalTreatmentsCompliant, nlisMovementsCompliant, feedCroppingCompliant });
        
        // Create a new checkboxes object to ensure reactivity
        const updatedCheckboxes = {
          ...this.checkboxes,
          biosecurityRecords: biosecurityCompliant,
          animalTreatments: animalTreatmentsCompliant,
          nlisMovements: nlisMovementsCompliant,
          feedCropping: feedCroppingCompliant
        };
        
        // Replace the entire checkboxes object to ensure Vue detects the change
        this.checkboxes = updatedCheckboxes;
        
        console.log('PreAuditGuidance: Checkboxes after update:', { ...this.checkboxes });
        
        // Also update the evidence checkboxes based on the findings
        // Create a new object with updated evidence checkboxes
        const evidenceUpdates = {
          photosEntry: biosecurityCompliant ? true : this.checkboxes.photosEntry,
          copiesTreatment: animalTreatmentsCompliant ? true : this.checkboxes.copiesTreatment,
          nlisReference: nlisMovementsCompliant ? true : this.checkboxes.nlisReference,
          supplierInvoices: feedCroppingCompliant ? true : this.checkboxes.supplierInvoices
        };
        
        // Update the checkboxes object with the evidence updates
        this.checkboxes = {
          ...this.checkboxes,
          ...evidenceUpdates
        };
        
        console.log('PreAuditGuidance: Checkboxes after evidence updates:', { ...this.checkboxes });
        
        // Expand the relevant sections to show the updated checkboxes
        this.expandedSections.dataGaps = true;
        this.expandedSections.requiredEvidence = true;
        
        // Force the component to re-render
        this.$nextTick(() => {
          console.log('PreAuditGuidance: Forcing component update');
          this.$forceUpdate();
        });
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      };
      
      // Read the file as text
      reader.readAsText(report);
    }
  }
}
</script>

<style>
/* Global styles to override checkbox color */
input[type="checkbox"]:checked {
  background-color: #3A895F !important;
  border-color: #3A895F !important;
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e") !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
}

input[type="checkbox"]:focus {
  --tw-ring-color: #3A895F !important;
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(58, 137, 95, 0.5) !important;
}

input[type="checkbox"] {
  color: #3A895F !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  border-radius: 0.25rem !important;
  border-width: 1px !important;
  border-color: #d1d5db !important;
  width: 1rem !important;
  height: 1rem !important;
}
</style>

<style scoped>
.pre-audit-guidance {
  min-height: 100vh;
  border-left: none !important;
}

.pre-audit-guidance * {
  border-left: none !important;
}
</style>
